import _uniqueId from 'lodash/uniqueId';

import React, { useState, useEffect, useRef, ReactElement } from "react";
import { FilterOperator } from "../../../backend/constants/filterOperators";
import FilterValuesList from "./FilterValuesList";
import IMFilesPropertyDefinition from "../../../backend/interfaces/mfiles/IMFilesPropertyDefinition";
import { iRootState, Dispatch } from "../../../rematch/store";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import SavedFiltersList from "./SavedFiltersList";
import { IMfilesPropertyFilter, IOwnPropertyFilter, isOwnPropertyFilter, FilterGroupConditionEnum, IFilterGroup } from "../../../backend/interfaces/filters/IFilter";
import {
  DRAG_HANDLE_CLASSNAME,
  DragHandle,
  FilterComponentHeader,
  FilterPanelHeader,
} from "./FiltersCommon";
import { SaveFilterButton } from "./SaveFilterButton";
import Draggable from "react-draggable";
import isString from "lodash/isString";
import { ownPropertiesToFilter, createFilterGroup } from "../../../backend/helpers/filteringHelper";
import { PropertyDataType } from "../../../backend/constants/datatypes";
import { IOwnPropertyDefinition } from "../../../backend/interfaces/filters/IOwnPropertyDefinition";
import { GridSecondaryButton, GridGenericButton } from "../common/Buttons";
import { translateMFilesString } from "../../../backend/helpers/translationHelpers";
import { IFiltersState } from "src/rematch/interfaces/states/IFiltersState";
import { setUrlFilter } from "src/gantt/helpers/FilterUtils";
import FilterPropertySelect from "./FilterPropertySelect";
import FilterOperatorSelect from "./FilterOperatorSelect";
import { AddFilter_SVG, 
  DeleteFilter_SVG, 
  FBGridWrapper, 
  FPGridContainer, 
  FPGridFooter, 
  FPGridHeader, 
  FPGridSubHeader, 
  FirstColumn, 
  GridButtonContainer, 
  GridButtonWrapper, 
  GridContainer, 
  GridInputContainer, 
  GridRow, 
  GridRowSpanThree, 
  SaveButtonContainer, 
  SecondColumn 
} from './FilterPanelCommon';
import { ReactComponent as IconTrash } from "../../../icons/icon-trash.svg";
import { ReactComponent as IconPlus } from "../../../icons/icon-plus.svg";

const GROUP_KEY_PREFIX = "fg-key-";
const GROUP_NAME_PREFIX = "filter-group-";
const FILTER_KEY_PREFIX = "filter-";

export interface IEditedFilter {
  key: string;
  mfilesPropertyIdOrOwnPropertyName?: number | string;
  operator?: FilterOperator;
  values: string[];
}

export interface IEditedFilterGroup {
  key?          : string;
  name          : string;
  conditionType : FilterGroupConditionEnum;
  filters        : IEditedFilter[];
}


interface InputWrapperProps {
  editedFilter: IEditedFilter;
  filterPanelProps: IFilterPanelContentProps;
  onDelete: (key: string) => void;
  updateEditedFilterInGroup: (editedFilter: IEditedFilter, filterIndex: string) => void;
  getPropertyDefinition: (propertyId: number) => IMFilesPropertyDefinition;
}


const InputWrapper = (props: InputWrapperProps ): ReactElement => {

  const editedFilter = props.editedFilter;
  const filterKey     = editedFilter.key;

  let propertyDefinition: IMFilesPropertyDefinition | IOwnPropertyDefinition = null;
  let dataType: PropertyDataType = null;

  const isOwnProperty = isString(editedFilter.mfilesPropertyIdOrOwnPropertyName);
  if (isOwnProperty) {
    propertyDefinition = ownPropertiesToFilter.find((p) => p.ownPropertyName === editedFilter.mfilesPropertyIdOrOwnPropertyName.toString());
    dataType = propertyDefinition?.dataType;
  } else {
    propertyDefinition = props.getPropertyDefinition(editedFilter.mfilesPropertyIdOrOwnPropertyName as number);
    dataType = propertyDefinition?.DataType;
  }

  const propertySelected    = editedFilter.mfilesPropertyIdOrOwnPropertyName != null;
  const operatorSelected    = editedFilter.operator != null;
  const valuesSelected      = editedFilter.values.length > 0;
  const operatorNeedsValues = [FilterOperator.EmptyOrNull, FilterOperator.NotEmptyOrNull].includes(editedFilter.operator) === false;
  const shouldShowValueList = operatorSelected && operatorNeedsValues;
  
  // We keep track of the state in group level
  const selectProperty = (property: number | string) => {

    props.updateEditedFilterInGroup({
      ...editedFilter,
      mfilesPropertyIdOrOwnPropertyName: property,
    }, filterKey); 
  };

  // Remove value selections, since they might be invalid for current property and operator
  const selectOperator = (operator: FilterOperator) => {

    props.updateEditedFilterInGroup({
      ...editedFilter,
      operator: operator,
    }, filterKey); 
  };


  const selectValues = (values: string[]) => {

    props.updateEditedFilterInGroup({
      ...editedFilter,
      values: values,
    }, filterKey); 
  };

  return (
    <GridRow>

      <GridInputContainer>

        <FilterPropertySelect
          viewIndex={props.filterPanelProps.viewIndex}
          selectedMfilesPropertyIdOrOwnPropertyName={editedFilter.mfilesPropertyIdOrOwnPropertyName}
          onChange={selectProperty}
        />
      
        <FilterOperatorSelect
          visible={editedFilter.mfilesPropertyIdOrOwnPropertyName != null}
          selectedOperator={editedFilter.operator}
          propertyDataType={dataType}
          onChange={selectOperator}
        />

        <FilterValuesList
          filterKey={filterKey}
          visible={shouldShowValueList}
          propertyDefinition={propertyDefinition}
          selectedOperator={editedFilter.operator}
          selectedValues={editedFilter.values}
          onChange={selectValues}
        />

        <DeleteFilter_SVG id={`delFilt-${filterKey}`} onClick={() => props.onDelete(filterKey)}>
          <IconTrash />
        </DeleteFilter_SVG>

      </GridInputContainer>

    </GridRow>
  )
}

interface IFilterPanelContentProps {
  viewIndex: number;
  closePanel: () => void;

  // From connect()
  userId: number;
  filters: IFiltersState;
  getPropertyDefinition: (propertyId: number) => IMFilesPropertyDefinition;
  viewName: (viewIndex: number) => string;
  translationKey: (viewIndex: number) => string;
  saveFilter: (filter: IFilterGroup, viewIndex: number) => void;
  setTemporaryFilter: (viewIndex: number, editedFilter: IEditedFilterGroup) => void;
  resetTemporaryFilter: (viewIndex: number) => void;
  resetEnabledFilters: (viewIndex: number) => void;
  getTemporaryFilter: (viewIndex: number) => IFilterGroup;
}

const FilterPanelContent = (props: IFilterPanelContentProps) => {

  const { t }   = useTranslation();
  const dragRef = useRef<HTMLDivElement>();

  let viewName         = props.viewName(props.viewIndex);
  const translationKey = props.translationKey(props.viewIndex);

  if ( translationKey !== undefined && translationKey != null ) {
    viewName = translateMFilesString(translationKey, viewName);
  }
  
  // The filter being edited right now. The state has to be here instead 
  // of FilterPanelContent in order to persist the state after closing modal
  const emptyEditedFilterGroup: IEditedFilterGroup = {
    key: _uniqueId( GROUP_KEY_PREFIX ),
    name: "",
    filters: [
      {
        key: _uniqueId( FILTER_KEY_PREFIX ),
        mfilesPropertyIdOrOwnPropertyName: null,
        operator: null,
        values: [],
      }
    ],
    conditionType: FilterGroupConditionEnum.AND,
  };

  const getEditedFilter = (): IEditedFilterGroup => {
    const temporaryFilterGroup = props.getTemporaryFilter(props.viewIndex);

    if ( temporaryFilterGroup === null ) {
      return emptyEditedFilterGroup;
    }

    let tempFiltersArray = [];

    for ( let tempFilter of temporaryFilterGroup.filters ) {
      tempFiltersArray.push({
        key: _uniqueId( FILTER_KEY_PREFIX ),
        mfilesPropertyIdOrOwnPropertyName: isOwnPropertyFilter(tempFilter)
          ? (tempFilter as IOwnPropertyFilter)?.ownPropertyName
          : (tempFilter as IMfilesPropertyFilter)?.propertyId,
        operator: tempFilter?.operator,
        values: tempFilter?.value.split(",") || [],
      })
    }

    return {
      key: _uniqueId( GROUP_KEY_PREFIX ),
      name: _uniqueId( GROUP_NAME_PREFIX ),
      conditionType: temporaryFilterGroup.conditionType,
      filters: tempFiltersArray
    };
  };

  const [editedFilterGroup, setEditedFilter] = useState<IEditedFilterGroup>(getEditedFilter());
  const [isSavedFilterLoaded, setIsSavedFilterLoaded] = useState(false);

  /*   
  useEffect(() => {
    setUrlFilter(props.filters);

    return;
  }, [props.filters]); */

  const clearFilter = () => {

    setIsSavedFilterLoaded( false );
    setEditedFilter(emptyEditedFilterGroup);

    props.resetTemporaryFilter(props.viewIndex);
    props.resetEnabledFilters(props.viewIndex);
  }


  // activate saved group filter
  const selectFilter = (panelFilterGroup: IFilterGroup, isCopy?: boolean) => {

    if ( ! panelFilterGroup ) {
      return setEditedFilter( emptyEditedFilterGroup );
    }

    setIsSavedFilterLoaded( true );

    let editedFilterArray = [];

    for ( let panelFilter of panelFilterGroup.filters ) {
      editedFilterArray.push({
        key: panelFilter.name,
        mfilesPropertyIdOrOwnPropertyName: isOwnPropertyFilter(panelFilter)
          ? (panelFilter as IOwnPropertyFilter)?.ownPropertyName
          : (panelFilter as IMfilesPropertyFilter)?.propertyId,
        operator: panelFilter?.operator,
        values: panelFilter?.value.split(",") || [],
      })
    }

    const editedFilterGroup: IEditedFilterGroup =  {
      key: isCopy ? _uniqueId( GROUP_KEY_PREFIX ) : panelFilterGroup.key || _uniqueId( GROUP_KEY_PREFIX ),
      name: isCopy ? "" : panelFilterGroup.name,
      conditionType: panelFilterGroup.conditionType,
      filters: editedFilterArray
    };

    setEditedFilter( editedFilterGroup );
  };

  const saveFilter = (filterName: string) => {
    
    props.saveFilter(
      createFilterGroup(
        props.viewIndex, 
        editedFilterGroup.key,
        filterName, 
        editedFilterGroup.conditionType, 
        editedFilterGroup.filters, 
        props.userId
      ),
      props.viewIndex
    );
    clearFilter();
  };

  //  apply current group filter without saving
  const setTemporaryFilter = (viewIndex: number, edited: IEditedFilterGroup) => {
    props.setTemporaryFilter(viewIndex, edited);
  }

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        props.closePanel()
      }
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  });

  const emptyFilter: IEditedFilter = {
    key: _uniqueId( FILTER_KEY_PREFIX ),
    mfilesPropertyIdOrOwnPropertyName: null,
    operator: null,
    values: [],
  }

  const onAddFilterButtonClick = () => {

    setEditedFilter(prev => ({
      ...prev,
      filters: [
        ...prev.filters,
        {
          key: _uniqueId( FILTER_KEY_PREFIX ),
          ...emptyFilter,
        }
      ]
    }));
  }

  // Delete filter from state if InputWraper's Trash icon was clicked
  const onDeleteFilterInput = (key: string) => {

    setEditedFilter(prev => ({
      ...prev,
      filters: prev.filters.filter((current) => current.key !== key)
    }));
  }


  // Remove value and operator selections, since they might be invalid for current property
  const updateEditedFilterInGroup = (editedFilter: IEditedFilter, filterKey: string) => {

    setEditedFilter( prev => ({
      ...prev,
      filters: prev.filters.map((filter) => 
        filter.key === filterKey
          ? { ...editedFilter }
          : { ...filter }
      )
    }));
  };

  /** Change condition Type of FilterGroup */
  const handleConditionTypeChange = (event) => {
    setEditedFilter( prev => ({
      ...prev,
      conditionType: event.target.value
    }));
  }

  const canUseFilter = true;

  return (
    <Draggable nodeRef={dragRef} handle={`.${DRAG_HANDLE_CLASSNAME}`}>

      <FBGridWrapper ref={dragRef}>

        <FPGridContainer >

          <FPGridHeader>
            <DragHandle>
              <FilterPanelHeader>{t("filter.objects", { name: viewName})}</FilterPanelHeader>
            </DragHandle>
            <FPGridSubHeader>
              <select style={{ verticalAlign: 'bottom', marginRight: '10px' }} onChange={handleConditionTypeChange} value={editedFilterGroup.conditionType} >
                <option value={ FilterGroupConditionEnum.AND }>{t("filter.matchallconditions")}</option>
                <option value={ FilterGroupConditionEnum.OR }>{t("filter.matchanyconditions")}</option>
              </select>
              <AddFilter_SVG onClick={onAddFilterButtonClick}>
                <IconPlus />
              </AddFilter_SVG>
            </FPGridSubHeader>
          </FPGridHeader>

          <GridContainer>

            <FirstColumn>

              <GridRow style={{ padding: '0' }}>
                <GridInputContainer>
                  <FilterComponentHeader>{t("filter.property")}</FilterComponentHeader>
                  <FilterComponentHeader>{t("filter.operator")}</FilterComponentHeader>
                  <FilterComponentHeader>{t("filter.value")}</FilterComponentHeader>
                </GridInputContainer>
              </GridRow>
              
              { editedFilterGroup.filters.map( (filter, index) => 
                <InputWrapper 
                  key={index}
                  editedFilter={ filter }
                  filterPanelProps={ props }
                  onDelete={ onDeleteFilterInput }
                  updateEditedFilterInGroup={ updateEditedFilterInGroup }
                  getPropertyDefinition={props.getPropertyDefinition}
                />
              ) }
            </FirstColumn>

            <SecondColumn>
              {/* Second Column that takes the size of 3 rows */}
              <GridRowSpanThree>
                <SavedFiltersList
                  viewIndex={props.viewIndex}
                  onChange={(selectedFilter: IFilterGroup) => selectFilter(selectedFilter)}
                  onCopy={(selectedFilter: IFilterGroup) => selectFilter(selectedFilter, true)}
                />
              </GridRowSpanThree>

            </SecondColumn>
            
          </GridContainer>

          <FPGridFooter>

            <GridButtonWrapper>

              <GridButtonContainer className="grid-button">
                <GridSecondaryButton onClick={clearFilter}>{t("command.clear")}</GridSecondaryButton>
              </GridButtonContainer>

              <GridButtonContainer className="grid-button">
                <GridSecondaryButton onClick={props.closePanel}>{t("command.close")}</GridSecondaryButton>
              </GridButtonContainer>

              <GridButtonContainer className="grid-button">
                <GridGenericButton disabled={!canUseFilter} onClick={() => setTemporaryFilter(props.viewIndex, editedFilterGroup)}>
                  {t("command.filter")}
                </GridGenericButton>
              </GridButtonContainer>

            </GridButtonWrapper>

            <SaveButtonContainer>
              <SaveFilterButton disabled={!canUseFilter} onSave={saveFilter} filterName={ isSavedFilterLoaded ? editedFilterGroup.name : "" } />
            </SaveButtonContainer>

          </FPGridFooter>
          
        </FPGridContainer>

      </FBGridWrapper>

    </Draggable>
  );
};


// TODO rewrite Filter related stuff to support 3D Array

export default connect(
  (state: iRootState) => ({
    getPropertyDefinition: (propertyId: number): IMFilesPropertyDefinition =>
      state.common.configuration.propertyDefinitions.get(propertyId),
    viewName: (viewIndex) => state.common.configuration.viewConfigurations[viewIndex].displayName,
    translationKey: (viewIndex) => state.common.configuration.viewConfigurations[viewIndex].translationKey,
    getTemporaryFilter: (viewIndex): IFilterGroup => state.filters.temporaryFilters[viewIndex],
    filters: state.filters,
    userId: state.common.configuration.userId
  }),
  (dispatch: Dispatch) => ({
    saveFilter: (filterGroup: IFilterGroup, viewIndex: number) => {
      dispatch.filters.savePanelFilterAsync({
        filterGroup: filterGroup,
        viewIndex: viewIndex,
      });
    },
    setTemporaryFilter: (viewIndex: number, editedFilter: IEditedFilterGroup) => {
      const filterGroup = createFilterGroup(
        viewIndex,
        `temp_fg_key_${viewIndex}`,
        `temporary_filter_group_${viewIndex}`,
        editedFilter.conditionType,
        editedFilter.filters,
      );
      dispatch.filters.updateTemporaryFilter({ viewIndex: viewIndex, filterGroup: filterGroup });
    },
    resetTemporaryFilter: (viewIndex: number) => dispatch.filters.resetTemporaryFilter({ viewIndex: viewIndex }),
    resetEnabledFilters: (viewIndex: number) => dispatch.filters.resetEnabledFilters({ viewIndex: viewIndex }),
  })
)(FilterPanelContent);
